import { RiskControlQuestionIdType } from '../store/ReportStore';

interface RiskControlQuestions {
  id: RiskControlQuestionIdType;
  question: string;
  selected: string | null;
}

interface RiskControlItems {
  header: string;
  questions: RiskControlQuestions[];
}

export const riskControlFormQuestions: RiskControlItems[] = [
  {
    header: 'Assessing the Risk Management Framework',
    questions: [
      {
        id: 'assessingRiskManagementFramework.establishedRiskManagementFramework',
        question: 'Has the company established a risk management framework?',
        selected: null,
      },
      {
        id: 'assessingRiskManagementFramework.boardPerceivesRiskManagementIntegral',
        question:
          'Does the board of directors and senior management perceive risk management as an integral part of objective setting and optimisation of performance?',
        selected: null,
      },
      {
        id: 'assessingRiskManagementFramework.ownershipDefinedAndAccepted',
        question:
          'Has risk management ownership been clearly defined and accepted by the employees concerned? Is it clear that the management of risk is an integral part of business management, owned by every manager, with the support and facilitation of the risk management staff?',
        selected: null,
      },
      {
        id: 'assessingRiskManagementFramework.rmcChairedByIndependentDirector',
        question:
          'Is there a Risk Management Committee (RMC) at board level chaired by an independent director?',
        selected: null,
      },
      {
        id: 'assessingRiskManagementFramework.rmcApprovedPolicies',
        question: 'Have risk management policies been approved by the RMC?',
        selected: null,
      },
      {
        id: 'assessingRiskManagementFramework.rmcDefinedRiskAppetite',
        question:
          'Have the company’s acceptable risk appetite (risk tolerance) or risk criteria been defined, by the RMC, where appropriate, and disseminated?',
        selected: null,
      },
      {
        id: 'assessingRiskManagementFramework.ceoChairedManagementCommittee',
        question:
          'Is there a Management Committee on risk management, chaired by the CEO (or equivalent)?',
        selected: null,
      },
      {
        id: 'assessingRiskManagementFramework.proceduresForManagingSignificantRisks',
        question:
          'Have procedures for managing significant risks been defined, approved by executive management and implemented in the company?',
        selected: null,
      },
      {
        id: 'assessingRiskManagementFramework.boardAwareOfHighRiskAreas',
        question:
          'Are the board and executive management aware of high-risk areas in the operations and strategies of the company and have these been properly documented and tracked?',
        selected: null,
      },
      {
        id: 'assessingRiskManagementFramework.riskProfilesEstablished',
        question: 'Have the risk profiles for the company been established?',
        selected: null,
      },
      {
        id: 'assessingRiskManagementFramework.legalAndRegulatoryObligationsIdentified',
        question:
          'Has the company identified its legal and regulatory obligations with regard to risk disclosure?',
        selected: null,
      },
      {
        id: 'assessingRiskManagementFramework.systemCharacteristicsForIdentifyingRisks',
        question:
          'Does the system for identifying and assessing risks have the following characteristics:\n• Systematic – formalised with sufficient level of appropriate detail\n• Comprehensive – encompassing all key areas of the company and reviewed on a regular basis\n• Integrated – linked to the core business process (e.g. business/strategic planning, contracting, mergers and acquisitions) within the company\n• Dynamic and iterative – repeated as necessary to ensure the assessment remains current in the midst of changing business conditions',
        selected: null,
      },
      {
        id: 'assessingRiskManagementFramework.systemForFinancialStatementRisks',
        question:
          'Has a system been established to identify significant risks affecting the preparation of the financial statements?',
        selected: null,
      },
      {
        id: 'assessingRiskManagementFramework.residualRiskReportedToBoard',
        question:
          'Are risks that exceed the acceptable limits or criteria defined by the company dealt with first? Has a residual risk level been defined and reported to the board?',
        selected: null,
      },
      {
        id: 'assessingRiskManagementFramework.majorRisksGiveRiseToActions',
        question:
          'Do major risks give rise to specific actions? Has the responsibility for such actions been defined? Where appropriate, is implementation of these actions monitored?',
        selected: null,
      },
      {
        id: 'assessingRiskManagementFramework.mechanismForChangingObjectives',
        question:
          'Is there a mechanism that makes it possible, when necessary in the light of changing business conditions and risks, for the company to make changes to the company’s objectives and business strategies?',
        selected: null,
      },
      {
        id: 'assessingRiskManagementFramework.earlyWarningIndicatorsInPlace',
        question:
          'Does the company have early warning key risk indicators (KRIs) in place to alert management (and the board as necessary) of significant changes in risk levels (e.g. political and economic upheavals, technological innovations resulting in the obsolescence of the company’s products or services, system failure, project delays, fraud, new product from competitors)?',
        selected: null,
      },
      {
        id: 'assessingRiskManagementFramework.boardDiscussesRiskProfiles',
        question:
          'Is the board meeting periodically with key management to discuss the key risk profiles of the company, the changing risk levels, changes to risk processes and the adequacy of internal control?',
        selected: null,
      },
      {
        id: 'assessingRiskManagementFramework.riskAssessmentResultsShared',
        question:
          'Are the results of risk assessment activities shared across the company for appropriate actions to be taken?',
        selected: null,
      },
      {
        id: 'assessingRiskManagementFramework.riskInformationCascadedToUnits',
        question:
          'Has appropriate risk information, including risk appetite or criteria and risk levels, been cascaded to all the operating units?',
        selected: null,
      },
      {
        id: 'assessingRiskManagementFramework.mandateScopeOfGovernanceFunctionsAligned',
        question:
          'To what extent are the mandate and scope of multiple governance functions in the company aligned to avoid overlap and ensure that there are no coverage gaps?',
        selected: null,
      },
    ],
  },
  {
    header: 'Control Environment and Control Activities',
    questions: [
      {
        id: 'controlEnvironmentAndActivities.cultureSupportsObjectives',
        question:
          "Do the company's culture, code of conduct, human resource policies and performance reward systems support the business objectives and the risk management and internal control system?",
        selected: null,
      },
      {
        id: 'controlEnvironmentAndActivities.seniorManagementCommitment',
        question:
          'Does senior management demonstrate, through its actions as well as its policies, the necessary commitment to competence, integrity and fostering a climate of trust within the company?',
        selected: null,
      },
      {
        id: 'controlEnvironmentAndActivities.authoritiesClearlyDefined',
        question:
          'Are authorities, responsibilities and accountabilities defined clearly so that decisions are made and actions taken by the appropriate people after due consideration of the risks involved and the approved risk appetite or criteria?',
        selected: null,
      },
      {
        id: 'controlEnvironmentAndActivities.decisionsAndActionsCoordinated',
        question:
          'Are the decisions and actions of different parts of the company appropriately co-ordinated?',
        selected: null,
      },
      {
        id: 'controlEnvironmentAndActivities.expectationsCommunicatedToEmployees',
        question:
          'Does the company communicate to its employees what is expected of them and the scope of their freedom to act? This may apply to areas such as customer relations; service levels for both internal and outsourced activities; health, safety and environmental protection; security of tangible and intangible assets; business continuity issues; expenditure matters; accounting; financial and other reporting.',
        selected: null,
      },
      {
        id: 'controlEnvironmentAndActivities.employeesHaveKnowledgeAndTools',
        question:
          "Do people in the company (and in its providers of outsourced services) have the knowledge, skills and tools to support the achievement of the company's objectives and to effectively manage risks that may affect the achievement of these objectives?",
        selected: null,
      },
      {
        id: 'controlEnvironmentAndActivities.businessContinuityProcessesInPlace',
        question:
          'Are business continuity management processes in place? Have these processes been periodically tested and communicated to relevant employees?',
        selected: null,
      },
      {
        id: 'controlEnvironmentAndActivities.successionPlanningEffective',
        question:
          'Are succession planning activities in place and operating effectively?',
        selected: null,
      },
    ],
  },
  {
    header: 'Information and Communication',
    questions: [
      {
        id: 'informationAndCommunication.boardReceivesTimelyReports',
        question:
          'Do the board and management receive timely, relevant and reliable reports on progress against business objectives and the related risks to enable them to make appropriate decisions? This could include reports with key performance indicators (KPIs) and indicators of changes in risk levels (KRIs), together with qualitative information such as customer satisfaction, conversion rates etc.',
        selected: null,
      },
      {
        id: 'informationAndCommunication.informationNeedsReassessed',
        question:
          'Are information needs and related information systems reassessed as objectives and related risks change or as reporting deficiencies are identified?',
        selected: null,
      },
      {
        id: 'informationAndCommunication.reportingProceduresEffective',
        question:
          "Are periodic reporting procedures, including quarterly and annual reporting, effective in communicating a clear account of the company's performance and the achievement of company’s objectives?",
        selected: null,
      },
      {
        id: 'informationAndCommunication.channelsForReportingBreaches',
        question:
          'Are there established channels of communication for individuals to report suspected breaches of law or regulations or other improprieties?',
        selected: null,
      },
      {
        id: 'informationAndCommunication.whistleblowingMechanismIndependent',
        question:
          'Is the whistleblowing mechanism independent of management and clearly communicated to all the stakeholders and employees?',
        selected: null,
      },
    ],
  },
  {
    header: 'Monitoring',
    questions: [
      {
        id: 'monitoring.ongoingProcessesEmbedded',
        question:
          "Are ongoing processes embedded within the company's overall business operations to monitor the effective application of the policies, processes and activities related to risk management and internal control? (Such processes may include control self-assessment, confirmation by personnel of compliance with policies and codes of conduct, or internal audit or other management reviews).",
        selected: null,
      },
      {
        id: 'monitoring.riskOwnersProvideAssurance',
        question:
          'Do risk owners have an obligation and a process to provide assurance to the board that they are adhering to the risk management and internal control framework?',
        selected: null,
      },
      {
        id: 'monitoring.processesReevaluateRisks',
        question:
          "Do these processes monitor the company's ability to re-evaluate risks and adjust controls effectively in response to changes in its objectives, its business, and its external environment?",
        selected: null,
      },
      {
        id: 'monitoring.effectiveFollowUpProcedures',
        question:
          'Are there effective follow-up procedures to ensure that appropriate change or action occurs in response to changes in risk and control assessments?',
        selected: null,
      },
      {
        id: 'monitoring.communicationToBoardOnMonitoringEffectiveness',
        question:
          'Is there appropriate communication to the board (or board committees such as RMC and AC) on the effectiveness of the ongoing monitoring processes on risk and control matters? This should include reporting any significant failings or weaknesses on a timely basis.',
        selected: null,
      },
      {
        id: 'monitoring.arrangementsForManagementMonitoring',
        question:
          "Are there specific arrangements for management monitoring and reporting to the board on risk and control matters of particular importance? These could include, for example, actual or suspected fraud and other illegal or irregular acts, or matters that could adversely affect the company's reputation or financial position.",
        selected: null,
      },
      {
        id: 'monitoring.ceoOrCfoProvidesAssurance',
        question:
          'Does the CEO/CFO (or their equivalent) provide assurance that the risk management and internal control framework is in place and operating effectively?',
        selected: null,
      },
    ],
  },
];
